'use client';

import Link from 'next/link';
import classNames from 'classnames';
import { Fragment, useCallback, useEffect, useState } from 'react';

import { usePathname } from 'next/navigation';
import useFeedbackModalStore from 'app/components/common/FeedbackModal/store';
import { IFile, IMenuItem, IMessenger, ISettings } from 'app/interfaces/common';
import { IBranchEssential } from 'app/interfaces/branches';
import ImageBg from 'app/components/common/ImageBg';
import MenuModal from './MenuModal';
import MessengersDropdown from './MessengersDropdown';

export default function Menu({
  items,
  branches,
  settings,
  socials,
  messengers,
  privacy_policy_file
}: {
  items: IMenuItem[];
  branches: IBranchEssential[];
  settings: ISettings | null;
  socials: IMenuItem[];
  messengers: IMessenger[];
  privacy_policy_file?: IFile | null;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const { open: openFeedbackModal } = useFeedbackModalStore();

  function onMouseEnter() {
    if (window.innerWidth < 1280) return;
    setIsHovered(true);
  }

  function onMouseLeave() {
    if (window.innerWidth < 1280 || isOpen) return;
    setIsHovered(false);
  }

  const close = useCallback(() => setIsOpen(false), []);

  const toggleOpen = useCallback(() => setIsOpen(isOpen => !isOpen), []);

  useEffect(() => {
    close();
    setIsHovered(false);
  }, [pathname, close]);

  return (
    <Fragment>
      <div className={classNames('flex gap-2')} onMouseLeave={onMouseLeave}>
        <button
          type="button"
          onClick={openFeedbackModal}
          className={classNames(
            'button button-press p-4 text-xs/[1em] uppercase rounded w-32 transition-all duration-400',
            'bg-sand-dark hover:bg-green text-graphite hover:text-white'
          )}
        >
          записаться
        </button>
        <MessengersDropdown messengers={messengers} isHovered={isHovered} />
        <div
          className={classNames(
            'flex gap-2 rounded bg-sand-dark transition-all duration-400',
            {
              'p-1': !isHovered,
              'p-3': isHovered
            }
          )}
          onMouseEnter={onMouseEnter}
        >
          <ul
            className={classNames(
              'hidden xl:flex gap-1 transition-all duration-400',
              {
                'gap-2': isHovered
              }
            )}
          >
            {items.map((item, index) => (
              <li key={index}>
                <Link
                  href={item.url}
                  className={classNames('pointer-events-none group', {
                    'pointer-events-auto': isHovered
                  })}
                >
                  <div
                    className={classNames(
                      'relative overflow-hidden rounded transition-all duration-400 isolate',
                      {
                        'w-10 h-14': !isHovered,
                        'w-25 h-32': isHovered
                      }
                    )}
                  >
                    {!!item.image?.url && (
                      <Fragment>
                        <div className="absolute inset-0 rounded group-hover:scale-110 transition-transform duration-600">
                          <ImageBg
                            src={item.image?.url}
                            alt={item.name}
                            fill
                            skeletonTheme="graphite"
                            placeholderDataURL={
                              item.image?.placeholder_data_url
                            }
                            className="object-cover rounded"
                          />
                        </div>

                        <div className="absolute inset-0 bg-black opacity-30 rounded"></div>
                      </Fragment>
                    )}

                    <div
                      className={classNames(
                        'absolute top-0 left-0 h-full overflow-hidden',
                        { 'w-0': !isHovered, 'w-full': isHovered }
                      )}
                    >
                      <div
                        className={classNames(
                          'py-3 px-2 text-xxs font-book uppercase text-white transition-opacity duration-600',
                          {
                            'opacity-0': !isHovered,
                            'opacity-100 delay-300': isHovered
                          }
                        )}
                      >
                        {item.name}
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className={classNames(
              'flex justify-center items-center w-10 min-h-10 group',
              {
                open: isOpen
              }
            )}
            onClick={toggleOpen}
          >
            <span className="flex flex-col gap-1">
              <span className="flex flex-col gap-1 relative">
                <span className="w-[3px] h-[3px] rounded-full bg-graphite absolute top-0 left-0 group-[&.open]:translate-x-2 transition-transform duration-300"></span>
                <span className="w-[3px] h-[3px] rounded-full bg-graphite left-0 group-[&.open]:-translate-x-2 transition-transform duration-300"></span>
                <span className="w-[3px] h-[3px] rounded-full bg-graphite"></span>
                <span className="w-[3px] h-[3px] rounded-full bg-graphite group-[&.open]:-translate-x-2 transition-transform duration-300"></span>
                <span className="w-[3px] h-[3px] rounded-full bg-graphite absolute bottom-0 left-0 group-[&.open]:translate-x-2 transition-transform duration-300"></span>
              </span>
            </span>
          </button>
        </div>
      </div>
      <MenuModal
        items={items}
        branches={branches}
        settings={settings}
        socials={socials}
        messengers={messengers}
        isOpen={isOpen}
        close={close}
        onOverlayClick={() => setIsHovered(false)}
        privacy_policy_file={privacy_policy_file}
      />
    </Fragment>
  );
}
